import React, { useState } from 'react';
import { X, Gift, Copy, Check, Loader2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin-only modal to mint a new gift code. Renders a QR + copy button.
 *
 * Additive — not integrated into any enforcement flow; used as a
 * utility for admins to grant access (trial or upgrade).
 */
export default function GiftCodeModal({ open, onClose }) {
  const [type, setType] = useState('trial');                 // 'trial' | 'upgrade'
  const [durationDays, setDurationDays] = useState(14);
  const [targetTier, setTargetTier] = useState('pro');       // 'pro' | 'premium'
  const [maxUses, setMaxUses] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const redeemUrl = result
    ? `${window.location.origin}${result.redeem_path || `/redeem?code=${result.code}`}`
    : '';

  const handleCreate = async () => {
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const token = localStorage.getItem('session_token');
      const payload = type === 'trial'
        ? { type, duration_days: Number(durationDays), max_uses: Number(maxUses) }
        : { type, target_tier: targetTier, max_uses: Number(maxUses) };
      const res = await axios.post(`${API}/admin/gift-codes`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create gift code.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!redeemUrl) return;
    try {
      await navigator.clipboard.writeText(redeemUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // clipboard may be blocked — no fallback needed for this admin-only tool
    }
  };

  const handleReset = () => {
    setResult(null);
    setError(null);
    setCopied(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="gift-code-modal">
      <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-[#1A1A1A]">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="text-lg font-bold text-[#F3EEE7]">Create Gift Code</h2>
          </div>
          <button onClick={onClose} className="text-[#777] hover:text-[#EAE7E1]" data-testid="gift-code-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Result */}
        {result ? (
          <div className="p-5 space-y-4" data-testid="gift-code-result">
            <div className="text-center">
              <img
                src={`data:image/png;base64,${result.qr_code_data_url}`}
                alt="Gift code QR"
                className="mx-auto w-48 h-48 border border-[#1A1A1A] bg-white p-2 rounded-sm"
                data-testid="gift-code-qr"
              />
              <div className="mt-4 font-mono text-2xl tracking-[0.25em] text-[#D4AF37]" data-testid="gift-code-value">{result.code}</div>
              <div className="mt-2 text-xs text-[#777]">
                {result.type === 'trial'
                  ? `Trial · ${result.duration_days} day${result.duration_days === 1 ? '' : 's'} · ${result.max_uses} use${result.max_uses === 1 ? '' : 's'}`
                  : `Upgrade → ${result.target_tier?.toUpperCase()} · ${result.max_uses} use${result.max_uses === 1 ? '' : 's'}`}
              </div>
            </div>

            <div className="bg-[#050505] border border-[#1A1A1A] rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold mb-1">Redemption URL</div>
              <div className="text-xs text-[#EAE7E1] break-all" data-testid="gift-code-url">{redeemUrl}</div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleCopy}
                variant="outline"
                className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm"
                data-testid="gift-code-copy"
              >
                {copied ? (<><Check className="w-4 h-4 mr-2" /> Copied</>) : (<><Copy className="w-4 h-4 mr-2" /> Copy URL</>)}
              </Button>
              <Button
                onClick={handleReset}
                variant="outline"
                className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm"
                data-testid="gift-code-create-another"
              >
                Create Another
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Code Type</label>
              <div className="grid grid-cols-2 gap-2 mt-2" data-testid="gift-code-type-select">
                {[
                  { v: 'trial',   label: 'Trial Access' },
                  { v: 'upgrade', label: 'Tier Upgrade' },
                ].map(o => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setType(o.v)}
                    className={`px-3 py-2 rounded-sm text-sm border transition-colors ${type === o.v ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'bg-[#050505] border-[#1A1A1A] text-[#AFAFAF] hover:border-[#333]'}`}
                    data-testid={`gift-code-type-${o.v}`}
                  >{o.label}</button>
                ))}
              </div>
            </div>

            {type === 'trial' && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Duration (days)</label>
                <input
                  type="number"
                  min={1}
                  max={3650}
                  value={durationDays}
                  onChange={e => setDurationDays(e.target.value)}
                  className="w-full mt-1.5 px-3 py-2 rounded-sm bg-[#050505] border border-[#1A1A1A] text-[#EAE7E1] focus:border-[#D4AF37] focus:outline-none"
                  data-testid="gift-code-duration-input"
                />
              </div>
            )}

            {type === 'upgrade' && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Target Tier</label>
                <div className="grid grid-cols-2 gap-2 mt-2" data-testid="gift-code-tier-select">
                  {['pro', 'premium'].map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTargetTier(t)}
                      className={`px-3 py-2 rounded-sm text-sm border transition-colors capitalize ${targetTier === t ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10' : 'bg-[#050505] border-[#1A1A1A] text-[#AFAFAF] hover:border-[#333]'}`}
                      data-testid={`gift-code-tier-${t}`}
                    >{t}</button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Max Uses</label>
              <input
                type="number"
                min={1}
                max={100000}
                value={maxUses}
                onChange={e => setMaxUses(e.target.value)}
                className="w-full mt-1.5 px-3 py-2 rounded-sm bg-[#050505] border border-[#1A1A1A] text-[#EAE7E1] focus:border-[#D4AF37] focus:outline-none"
                data-testid="gift-code-max-uses-input"
              />
              <p className="text-[11px] text-[#555] mt-1">How many different accounts can redeem this code.</p>
            </div>

            {error && (
              <div className="text-xs text-[#C44545] bg-[#C44545]/5 border border-[#C44545]/25 rounded-sm p-2" data-testid="gift-code-error">{error}</div>
            )}

            <Button
              onClick={handleCreate}
              disabled={submitting}
              className="w-full bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 disabled:opacity-50 text-[#050505] rounded-sm font-bold"
              data-testid="gift-code-create-button"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating&hellip;</>) : (<>Create Gift Code <ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
